import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Bell, BellRing, Search, Trash2, Loader2, Mail, AlertCircle, Bookmark, MapPin, ArrowRight, CheckCircle } from 'lucide-react';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SEO from '@/components/SEO';
import { API } from '@/App';
import { toast } from 'sonner';

const AlertsDashboardPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email');
  
  const [email, setEmail] = useState(emailFromUrl || '');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  // Try to get email from localStorage
  useEffect(() => {
    if (!email) {
      const savedEmail = localStorage.getItem('ikonic_price_alert_email') || 
                         localStorage.getItem('ikonic_newsletter_email');
      if (savedEmail) {
        setEmail(savedEmail);
      }
    }
  }, [email]);

  // Auto-load if email from URL
  useEffect(() => {
    if (emailFromUrl) {
      loadAlerts(emailFromUrl);
    }
  }, [emailFromUrl]);

  const loadAlerts = async (emailToLoad) => {
    const targetEmail = emailToLoad || email;
    if (!targetEmail.trim() || !targetEmail.includes('@')) {
      toast.error('Por favor ingresa un email válido');
      return;
    }

    setLoadingData(true);
    try {
      // Load price alerts
      const alertsRes = await axios.get(`${API}/price-alerts/my-alerts?email=${encodeURIComponent(targetEmail)}`);
      setPriceAlerts(alertsRes.data.alerts || []);

      // Load saved searches
      const searchesRes = await axios.get(`${API}/saved-searches?email=${encodeURIComponent(targetEmail)}`);
      setSavedSearches(searchesRes.data.searches || []);

      // Save email for future use
      localStorage.setItem('ikonic_price_alert_email', targetEmail.toLowerCase().trim());
      
      setIsVerified(true);
    } catch (error) {
      console.error('Error loading alerts:', error);
      toast.error('Error al cargar las alertas');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    loadAlerts();
  };

  const deletePriceAlert = async (propertyId) => {
    setDeletingId(`price-${propertyId}`);
    try {
      await axios.delete(`${API}/price-alerts/unsubscribe?property_id=${propertyId}&email=${encodeURIComponent(email)}`);
      setPriceAlerts(prev => prev.filter(a => a.property_id !== propertyId));
      toast.success('Alerta eliminada');
    } catch (error) {
      toast.error('Error al eliminar la alerta');
    } finally {
      setDeletingId(null);
    }
  };

  const deleteSavedSearch = async (searchId) => {
    setDeletingId(`search-${searchId}`);
    try {
      await axios.delete(`${API}/saved-searches/${searchId}?email=${encodeURIComponent(email)}`);
      setSavedSearches(prev => prev.filter(s => s.id !== searchId));
      toast.success('Búsqueda eliminada');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tienes permiso para eliminar esta búsqueda');
      } else {
        toast.error('Error al eliminar la búsqueda');
      }
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-GT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Email entry view
  if (!isVerified) {
    return (
      <>
        <SEO 
          title="Mis Alertas y Búsquedas | Ikonic Deals"
          description="Gestiona tus alertas de precio y búsquedas guardadas"
          noindex={true}
        />
        <Header />
        
        <section className="pt-32 pb-24 min-h-screen">
          <div className="container-custom px-4 sm:px-6 max-w-md mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Bell size={40} className="text-[#D4AF37]" />
              </div>
              <h1 className="text-2xl text-white mb-4">
                {t('dashboard.title', 'Mis Alertas y Búsquedas')}
              </h1>
              <p className="text-[#A3A3A3] mb-8">
                {t('dashboard.enterEmail', 'Ingresa tu email para ver tus alertas de precio y búsquedas guardadas.')}
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={18} />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-[#121212] border-white/10 text-white"
                    placeholder="tu@email.com"
                    required
                    data-testid="dashboard-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loadingData}
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#FFE182]"
                  data-testid="load-alerts-btn"
                >
                  {loadingData ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Cargando...
                    </>
                  ) : (
                    <>
                      <Search size={18} className="mr-2" />
                      Ver mis alertas
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </section>

        <Footer />
      </>
    );
  }

  // Dashboard view
  return (
    <>
      <SEO 
        title="Mis Alertas y Búsquedas | Ikonic Deals"
        description="Gestiona tus alertas de precio y búsquedas guardadas"
        noindex={true}
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-8 bg-gradient-to-b from-[#0a0a0a] to-[#050505]">
        <div className="container-custom px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <span className="text-[#D4AF37] text-sm uppercase tracking-widest">
                {t('dashboard.subtitle', 'Tu Panel')}
              </span>
              <h1 className="mt-3 text-3xl md:text-4xl text-white flex items-center gap-3">
                <Bell className="text-[#D4AF37]" size={32} />
                {t('dashboard.title', 'Mis Alertas y Búsquedas')}
              </h1>
              <p className="mt-2 text-[#A3A3A3] flex items-center gap-2">
                <Mail size={16} />
                {email}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setIsVerified(false)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cambiar email
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-6 border-b border-white/5">
        <div className="container-custom px-4 sm:px-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#121212] p-4 rounded-lg border border-white/5">
              <div className="flex items-center gap-3">
                <BellRing size={24} className="text-[#D4AF37]" />
                <div>
                  <p className="text-[#D4AF37] text-2xl font-mono">{priceAlerts.length}</p>
                  <p className="text-[#A3A3A3] text-sm">Alertas de precio</p>
                </div>
              </div>
            </div>
            <div className="bg-[#121212] p-4 rounded-lg border border-white/5">
              <div className="flex items-center gap-3">
                <Bookmark size={24} className="text-[#D4AF37]" />
                <div>
                  <p className="text-[#D4AF37] text-2xl font-mono">{savedSearches.length}</p>
                  <p className="text-[#A3A3A3] text-sm">Búsquedas guardadas</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Price Alerts Section */}
      <section className="py-8">
        <div className="container-custom px-4 sm:px-6">
          <h2 className="text-xl text-white mb-4 flex items-center gap-2">
            <BellRing size={20} className="text-[#D4AF37]" />
            Alertas de Precio
          </h2>
          
          {priceAlerts.length === 0 ? (
            <div className="text-center py-8 bg-[#121212] rounded-lg border border-white/5">
              <AlertCircle size={40} className="mx-auto text-white/20 mb-3" />
              <p className="text-[#A3A3A3]">No tienes alertas de precio activas</p>
              <Link to="/propiedades" className="text-[#D4AF37] text-sm hover:underline mt-2 inline-block">
                Explorar propiedades
              </Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="price-alerts-list">
              {priceAlerts.map((alert) => (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#121212] border border-white/5 p-4 rounded-lg hover:border-[#D4AF37]/30 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <Link 
                        to={`/propiedad/${alert.property_id}`}
                        className="text-white font-medium hover:text-[#D4AF37] transition-colors"
                      >
                        {alert.property_name}
                      </Link>
                      <p className="text-[#D4AF37] font-mono mt-1">
                        Precio al suscribirte: {formatPrice(alert.subscribed_price)}
                      </p>
                      <p className="text-[#A3A3A3] text-xs mt-2">
                        Creada el {formatDate(alert.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={() => deletePriceAlert(alert.property_id)}
                      disabled={deletingId === `price-${alert.property_id}`}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                      data-testid={`delete-price-alert-${alert.id}`}
                    >
                      {deletingId === `price-${alert.property_id}` ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Trash2 size={18} />
                      )}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Saved Searches Section */}
      <section className="py-8 pb-24">
        <div className="container-custom px-4 sm:px-6">
          <h2 className="text-xl text-white mb-4 flex items-center gap-2">
            <Bookmark size={20} className="text-[#D4AF37]" />
            Búsquedas Guardadas
          </h2>
          
          {savedSearches.length === 0 ? (
            <div className="text-center py-8 bg-[#121212] rounded-lg border border-white/5">
              <Search size={40} className="mx-auto text-white/20 mb-3" />
              <p className="text-[#A3A3A3]">No tienes búsquedas guardadas</p>
              <Link to="/propiedades" className="text-[#D4AF37] text-sm hover:underline mt-2 inline-block">
                Buscar propiedades
              </Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="saved-searches-list">
              {savedSearches.map((search) => (
                <motion.div
                  key={search.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#121212] border border-white/5 p-4 rounded-lg hover:border-[#D4AF37]/30 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-white font-medium">{search.name}</p>
                      
                      {/* Search criteria tags */}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {search.criteria?.property_type && (
                          <span className="bg-[#D4AF37]/20 text-[#D4AF37] text-xs px-2 py-1 rounded">
                            {search.criteria.property_type}
                          </span>
                        )}
                        {search.criteria?.transaction_type && (
                          <span className="bg-white/10 text-white/80 text-xs px-2 py-1 rounded">
                            {search.criteria.transaction_type}
                          </span>
                        )}
                        {search.criteria?.department && (
                          <span className="bg-white/10 text-white/80 text-xs px-2 py-1 rounded flex items-center gap-1">
                            <MapPin size={10} />
                            {search.criteria.department}
                          </span>
                        )}
                        {search.criteria?.min_price && (
                          <span className="bg-white/10 text-white/80 text-xs px-2 py-1 rounded">
                            Desde {formatPrice(parseInt(search.criteria.min_price))}
                          </span>
                        )}
                        {search.criteria?.max_price && (
                          <span className="bg-white/10 text-white/80 text-xs px-2 py-1 rounded">
                            Hasta {formatPrice(parseInt(search.criteria.max_price))}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 mt-3 text-xs text-[#A3A3A3]">
                        <span>Creada el {formatDate(search.created_at)}</span>
                        {search.matches_notified > 0 && (
                          <span className="flex items-center gap-1 text-green-400">
                            <CheckCircle size={12} />
                            {search.matches_notified} propiedades notificadas
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteSavedSearch(search.id)}
                      disabled={deletingId === `search-${search.id}`}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                      data-testid={`delete-saved-search-${search.id}`}
                    >
                      {deletingId === `search-${search.id}` ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Trash2 size={18} />
                      )}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
};

export default AlertsDashboardPage;
